import { Box, Button, useToast, VStack } from "@chakra-ui/react";
import { useAddAccountMutation, useGetBanksQuery, useResolveBankAcctMutation } from "apis";
import { InputLabel, Input, Select, ReactSelect } from "components";
import { useAuthToken, useRedebounce } from "hooks";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setNewTransferId } from "store/slices";

interface IProps {
  changeTab?: (index: number) => void;
  fiatType?: string;
  hideCountry?: boolean;
}
const AddFiatAccount = ({ changeTab, fiatType, hideCountry }: IProps) => {
  const { authToken } = useAuthToken();
  const toast = useToast();
  const [_addFiatAccount, { isLoading }] = useAddAccountMutation();

  const defaultCountry = useMemo(() => {
    const mapCountry: any = {
      ngn: "ng",
      kes: "ke",
      ghs: "gh",
    };

    const fiat = fiatType?.toLowerCase() ?? "ngn";

    return mapCountry[fiat] as string;
  }, [fiatType]);

  const [state, setState] = useState({
    accountName: "",
    accountNumber: "",
    country: defaultCountry,
    institutionName: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value, name } = e.target;

    setState({
      ...state,
      [name]: value,
    });
  };

  const dispatch = useDispatch();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { accountName, accountNumber, country, institutionName } = state;
    try {
      const response = await _addFiatAccount({
        accountName,
        accountNumber,
        country,
        institutionName,
        token: authToken!,
      }).unwrap();

      if (response?.data) {
        dispatch(setNewTransferId(response.data.fiatAccountId));
        toast({
          position: "bottom-right",
          title: "Success",
          description: response?.message,
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        changeTab && changeTab(1);
      }
    } catch (error) {}
  };

  const isDisabled = useMemo(() => {
    return !state.accountName || !state.accountNumber || !state.institutionName;
  }, [state]);

  const { data, isSuccess } = useGetBanksQuery(defaultCountry!, {
    skip: !defaultCountry,
  });

  const [_bankCode, setBankCode] = useState("");

  const handleBankName = (selectedOption: { label: string; value: string; code: string }) => {
    setBankCode(selectedOption.code);
    setState((prevState) => ({
      ...prevState,
      institutionName: selectedOption.value,
    }));
  };

  const bankData = useMemo(
    () =>
      data?.bankPayloads?.map(({ bankCode, bankName }) => {
        const options = {
          label: `${bankName}`,
          value: bankName,
          code: bankCode,
        };

        return options;
      })!,
    [data]
  );

  const [_resolveBankAcct, { isLoading: resolveLoading, data: resolvedData }] = useResolveBankAcctMutation();

  const redebounce = useRedebounce();

  useEffect(() => {
    const handleResolvedData = async () => {
      try {
        const response = await _resolveBankAcct({
          bankCode: _bankCode,
          bankAccountNumber: state.accountNumber,
        }).unwrap();

        setState({
          ...state,
          accountName: response.resolved.account_name,
        });
      } catch (error) {}
    };

    _bankCode && state.accountNumber.length === 10 && redebounce(() => handleResolvedData(), "resolveBank", 500)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_resolveBankAcct, _bankCode, redebounce, state.accountName, state.accountNumber]);

  return (
    <Box>
      <Box mt="16px">
        <form onSubmit={handleSubmit}>
          <VStack spacing="24px">
            {!hideCountry && (
              <Box width="100%">
                <InputLabel htmlFor="country">Country</InputLabel>
                <Select id="country" name="country" onChange={handleChange} isDisabled defaultChecked defaultValue={defaultCountry}>
                  <option value="ng">Nigeria</option>
                  <option value="ke">Kenya</option>
                  <option value="gh">Ghana</option>
                </Select>
              </Box>
            )}

            <Box width="100%">
              {/* <InputLabel htmlFor="institutionName">Bank Name</InputLabel> */}

              {/* <Select name="institutionName" id="institutionName" onChange={handleChange}>
                <option>Bank Name</option>
                {data?.bankPayloads?.map(({ bankCode, bankName }, index) => {
                  return (
                    <option value={bankName} key={`${bankCode}__${index}`}>
                      {bankName}
                    </option>
                  );
                })}
              </Select> */}

              <ReactSelect
                options={bankData}
                onChange={handleBankName}
                placeholder={isSuccess ? data?.bankPayloads[0]?.bankName! : ""}
                label="Bank Name"
                name="institutionName"
                onInputChange={(e: string) => e}
              />
            </Box>

            <Box width="100%">
              <InputLabel htmlFor="accountNumber">Account Number</InputLabel>
              <Input
                onChange={handleChange}
                isRequired
                type="number"
                id="accountNumber"
                name="accountNumber"
                placeholder="Account Number"
                minH={{ base: "56px", "2sm": "60px" }}
              />
            </Box>

            <Box width="100%">
              <InputLabel htmlFor="accountName" isLoading={state?.country === "ng" ? resolveLoading : false}>
                Account Name
              </InputLabel>
              <Input
                onChange={handleChange}
                isRequired
                id="accountName"
                name="accountName"
                placeholder="Account Name"
                minH={{ base: "56px", "2sm": "60px" }}
                value={state?.country === "ng" ? resolvedData?.resolved?.account_name : ""}
                isDisabled={state?.country === "ng" ? true : false}
              />
            </Box>
          </VStack>

          <Button
            mt="42px"
            width="100%"
            type="submit"
            minW={{ base: "auto", "2sm": "400px" }}
            isDisabled={isDisabled}
            isLoading={isLoading}
          >
            Submit
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default AddFiatAccount;
